(() => {
  const rootSelector = '[data-meerbegleiter-configurator]';
  const allowedModelIds = new Set(['classic']);
  const allowedDiameterMm = new Set([8]);

  const initialize = (root) => {
    if (root.dataset.meerbegleiterReady === 'true') return;
    root.dataset.meerbegleiterReady = 'true';

    const catalogNode = root.querySelector('[data-meerbegleiter-catalog]');
    const form = root.querySelector('[data-meerbegleiter-form]');
    if (!catalogNode || !form) return;

    const review = root.querySelector('.suedlis-meerbegleiter-personalizer__review');
    if (review && document.body.classList.contains('template-collection') && typeof window.ResizeObserver === 'function') {
      const updateReviewHeight = () => {
        review.style.setProperty('--suedlis-review-height', Math.ceil(review.getBoundingClientRect().height) + 'px');
        review.dataset.meerbegleiterStickyReady = 'true';
      };
      const reviewObserver = new ResizeObserver(updateReviewHeight);
      reviewObserver.observe(review);
      updateReviewHeight();
    }

    let catalog;
    try {
      catalog = JSON.parse(catalogNode.textContent);
    } catch (_error) {
      const formError = root.querySelector('[data-meerbegleiter-form-error]');
      if (formError) {
        formError.textContent = 'Die Konfigurationsdaten konnten nicht geladen werden.';
        formError.hidden = false;
      }
      return;
    }
    const shopifyDraft = window.SuedlisMeerbegleiterShopifyDraft || null;
    catalog.models = (Array.isArray(catalog.models) ? catalog.models : [])
      .filter((model) => model.active && allowedModelIds.has(model.id))
      .map((model) => ({
        ...model,
        availableDiameters: (model.availableDiameters || []).map(Number)
          .filter((diameterMm) => allowedDiameterMm.has(diameterMm))
      }));
    catalog.ropeDiameters = (Array.isArray(catalog.ropeDiameters) ? catalog.ropeDiameters : [])
      .filter((entry) => allowedDiameterMm.has(Number(entry.diameterMm)));
    catalog.ropes = (Array.isArray(catalog.ropes) ? catalog.ropes : [])
      .filter((entry) => allowedDiameterMm.has(Number(entry.diameterMm)));
    catalog.fittings = (Array.isArray(catalog.fittings) ? catalog.fittings : [])
      .filter((entry) => allowedDiameterMm.has(Number(entry.diameterMm)));
    // Scope the shared variant table to this engraving configurator only.
    const shopifyDraftConfig = {
      ...shopifyDraft?.config,
      baseVariants: (shopifyDraft?.config?.baseVariants || [])
        .filter((variant) => allowedModelIds.has(variant.modelId) && allowedDiameterMm.has(Number(variant.diameterMm)))
    };
    catalog.shopifyConfigurations = shopifyDraftConfig.baseVariants;
    catalog.extrasConfig = catalog.extrasConfig && typeof catalog.extrasConfig === 'object' ? catalog.extrasConfig : {};
    catalog.extrasConfig.availableDiameters = Array.isArray(shopifyDraftConfig.extrasAvailableDiameters)
      ? shopifyDraftConfig.extrasAvailableDiameters.map(Number)
      : Array.isArray(catalog.extrasConfig.availableDiameters)
        ? catalog.extrasConfig.availableDiameters.map(Number)
        : [];
    catalog.extrasConfig.finishes = Array.isArray(catalog.extrasConfig.finishes) ? catalog.extrasConfig.finishes : [];
    const extraInventoryBySku = new Map((Array.isArray(catalog.extrasConfig.inventory) ? catalog.extrasConfig.inventory : [])
      .filter((entry) => entry && typeof entry.sku === 'string' && entry.sku)
      .map((entry) => [entry.sku, entry]));
    catalog.extrasConfig.items = (Array.isArray(catalog.extrasConfig.items) ? catalog.extrasConfig.items : [])
      .map((item) => {
        const variant = shopifyDraft?.resolveExtraVariant?.(item.id, shopifyDraftConfig) || null;
        const inventory = variant?.sku ? extraInventoryBySku.get(variant.sku) : null;
        return variant
          ? {
            ...item,
            ...variant,
            inventoryTracked: inventory?.inventoryTracked === true,
            inventoryPolicy: inventory?.inventoryPolicy || '',
            inventoryQuantity: Number.isInteger(Number(inventory?.inventoryQuantity)) ? Number(inventory.inventoryQuantity) : null,
            availableForSale: inventory?.availableForSale === true,
            requiresShipping: inventory?.requiresShipping === true
          }
          : item;
      });
    const previewContext = catalog.previewContext && typeof catalog.previewContext === 'object'
      ? catalog.previewContext
      : {};
    const browserTheme = window.Shopify?.theme || {};
    const renderedEnvironmentContext = () => ({
      renderedThemeId: previewContext.themeId,
      renderedThemeRole: previewContext.themeRole,
      browserThemeId: window.Shopify?.theme?.id,
      browserThemeRole: window.Shopify?.theme?.role
    });
    const cartEnvironment = shopifyDraft?.resolveCartEnvironment?.({
      renderedThemeId: previewContext.themeId,
      renderedThemeRole: previewContext.themeRole,
      browserThemeId: browserTheme.id,
      browserThemeRole: browserTheme.role
    }, shopifyDraftConfig) || null;
    const previewTestDataEnabled = Boolean(
      cartEnvironment?.kind === 'preview'
      && cartEnvironment.previewTestDataEnabled === true
      && catalog.previewTestDataEnabled === true
    );
    const currentCartEnvironment = () => shopifyDraft?.resolveCartEnvironment?.(
      renderedEnvironmentContext(),
      shopifyDraftConfig
    ) || null;
    const isCurrentCartEnvironmentAllowed = () => Boolean(currentCartEnvironment());

    if (previewTestDataEnabled) {
      catalog.models.filter((model) => model.active && model.requiredFittingTypes?.length > 0).forEach((model) => {
        const fittingType = model.requiredFittingTypes[0];
        const engravingSource = model.engravingAvailable
          ? catalog.fittings.find((fitting) => fitting.engravable && fitting.engraving)?.engraving || null
          : null;
        (model.availableDiameters || []).map(Number).forEach((diameterMm) => {
          const exists = catalog.fittings.some((fitting) => (
            fitting.active
            && fitting.type === fittingType
            && fitting.compatibleModelIds?.includes(model.id)
            && fitting.compatibleDiameters?.map(Number).includes(diameterMm)
          ));
          if (exists) return;
          const isCenterpiece = fittingType === 'centerpiece';
          catalog.fittings.push({
            id: `${fittingType}-test-${model.id}-${diameterMm}`,
            name: isCenterpiece ? 'Mittelstück – Testdarstellung' : 'Endstück – Testdarstellung',
            displayName: isCenterpiece ? 'Mittelstück – Testdarstellung' : 'Endstück – Testdarstellung',
            type: fittingType,
            typeLabel: isCenterpiece ? 'Mittelstück' : 'Endstück',
            imageAssetName: null,
            imageUrl: null,
            imageStatus: 'test-placeholder',
            finish: 'Bildplatzhalter',
            material: null,
            diameterMm,
            compatibleModelIds: [model.id],
            compatibleDiameters: [diameterMm],
            engravable: Boolean(engravingSource),
            engraving: engravingSource ? { ...engravingSource, position: 'Test-Gravurfläche', testOnly: true } : null,
            engravingProfileId: engravingSource ? 'preview-test' : null,
            engravingApprovalStatus: 'test-only',
            shopifyVariantId: null,
            priceCents: null,
            inventoryStatus: 'test-only',
            sortOrder: 900 + diameterMm,
            active: true,
            testOnly: true
          });
        });
      });
    }

    const state = {
      modelId: catalog.models.some((model) => model.id === 'classic') ? 'classic' : '',
      diameterMm: null,
      ropeId: '',
      fittingId: '',
      engravingEnabled: false,
      engravingMode: 'text',
      extrasEnabled: false,
      extrasFinish: '',
      extras: []
    };

    const elements = {
      diameterOptions: root.querySelector('[data-meerbegleiter-diameter-options]'),
      ropeOptions: root.querySelector('[data-meerbegleiter-rope-modal-options]'),
      fittingOptions: root.querySelector('[data-meerbegleiter-fitting-options]'),
      ropeStatus: root.querySelector('[data-meerbegleiter-rope-status]'),
      fittingStatus: root.querySelector('[data-meerbegleiter-fitting-status]'),
      rope: root.querySelector('[data-meerbegleiter-rope]'),
      ropeSelection: root.querySelector('[data-meerbegleiter-rope-selection]'),
      ropeSelectionMedia: root.querySelector('[data-meerbegleiter-rope-selection-media]'),
      ropeSelectionImage: root.querySelector('[data-meerbegleiter-rope-selection-image]'),
      ropeSelectionName: root.querySelector('[data-meerbegleiter-rope-selection-name]'),
      ropeSelectionMeta: root.querySelector('[data-meerbegleiter-rope-selection-meta]'),
      ropeModal: root.querySelector('[data-meerbegleiter-rope-modal]'),
      ropeModalDialog: root.querySelector('[data-meerbegleiter-rope-modal-dialog]'),
      ropeModalTitle: root.querySelector('[data-meerbegleiter-rope-modal-title]'),
      ropeModalDiameter: root.querySelector('[data-meerbegleiter-rope-modal-diameter]'),
      ropeModalStatus: root.querySelector('[data-meerbegleiter-rope-modal-status]'),
      ropeModalTrigger: root.querySelector('[data-meerbegleiter-open-rope-modal]'),
      personalizationStep: root.querySelector('[data-meerbegleiter-personalization-step]'),
      stepNodes: Array.from(root.querySelectorAll('[data-meerbegleiter-step]')),
      extrasStep: root.querySelector('[data-meerbegleiter-extras-step]'),
      extrasControls: root.querySelector('[data-meerbegleiter-extras-controls]'),
      extrasDisplay: root.querySelector('[data-meerbegleiter-extras-display]'),
      extrasPrice: root.querySelector('[data-meerbegleiter-extras-price]'),
      extrasOverviewImages: root.querySelector('[data-meerbegleiter-extras-overview-images]'),
      extrasModal: root.querySelector('[data-meerbegleiter-extras-modal]'),
      extrasModalDialog: root.querySelector('[data-meerbegleiter-extras-modal-dialog]'),
      extrasModalDisplay: root.querySelector('[data-meerbegleiter-extras-modal-display]'),
      extrasModalPrice: root.querySelector('[data-meerbegleiter-extras-modal-price]'),
      extrasInput: root.querySelector('[data-meerbegleiter-extras-input]'),
      extrasError: root.querySelector('[data-meerbegleiter-extras-error]'),
      extrasTokens: root.querySelector('[data-meerbegleiter-extras-tokens]'),
      extrasReset: root.querySelector('[data-meerbegleiter-reset-extras]'),
      extrasOpen: root.querySelector('[data-meerbegleiter-open-extras-modal]'),
      extrasDone: root.querySelector('[data-meerbegleiter-extras-modal-done]'),
      extrasSpecialButtons: Array.from(root.querySelectorAll('[data-meerbegleiter-add-extra-special]')),
      engravingStep: root.querySelector('[data-meerbegleiter-engraving-step]'),
      engravingControls: root.querySelector('[data-meerbegleiter-engraving-controls]'),
      detailsTitle: root.querySelector('[data-meerbegleiter-details-title]'),
      preview: root.querySelector('[data-meerbegleiter-preview]'),
      previewStep: root.querySelector('[data-meerbegleiter-preview-step]'),
      previewSurface: root.querySelector('[data-meerbegleiter-preview-surface]'),
      previewPosition: root.querySelector('[data-meerbegleiter-preview-position]'),
      previewText: root.querySelector('[data-meerbegleiter-preview-text]'),
      text: root.querySelector('[data-meerbegleiter-text]'),
      place: root.querySelector('[data-meerbegleiter-place]'),
      latitudeDegrees: root.querySelector('[data-meerbegleiter-latitude-degrees]'),
      latitudeMinutes: root.querySelector('[data-meerbegleiter-latitude-minutes]'),
      latitudeDirection: root.querySelector('[data-meerbegleiter-latitude-direction]'),
      longitudeDegrees: root.querySelector('[data-meerbegleiter-longitude-degrees]'),
      longitudeMinutes: root.querySelector('[data-meerbegleiter-longitude-minutes]'),
      longitudeDirection: root.querySelector('[data-meerbegleiter-longitude-direction]'),
      confirmation: root.querySelector('[data-meerbegleiter-confirmation]'),
      withdrawalNotice: root.querySelector('[data-meerbegleiter-withdrawal-note]'),
      textCount: root.querySelector('[data-meerbegleiter-text-count]'),
      placeCount: root.querySelector('[data-meerbegleiter-place-count]'),
      textLimit: root.querySelector('[data-meerbegleiter-text-limit]'),
      placeLimit: root.querySelector('[data-meerbegleiter-place-limit]'),
      summaryEmpty: root.querySelector('[data-meerbegleiter-summary-empty]'),
      availability: root.querySelector('[data-meerbegleiter-availability]'),
      submit: root.querySelector('[data-meerbegleiter-submit]'),
      formError: root.querySelector('[data-meerbegleiter-form-error]'),
      variantId: root.querySelector('[data-meerbegleiter-variant-id]')
    };

    const properties = {
      model: root.querySelector('[data-meerbegleiter-property-model]'),
      modelId: root.querySelector('[data-meerbegleiter-property-model-id]'),
      rope: root.querySelector('[data-meerbegleiter-property-rope]'),
      ropeId: root.querySelector('[data-meerbegleiter-property-rope-id]'),
      diameter: root.querySelector('[data-meerbegleiter-property-diameter]'),
      fitting: root.querySelector('[data-meerbegleiter-property-fitting]'),
      fittingId: root.querySelector('[data-meerbegleiter-property-fitting-id]'),
      fittingSize: root.querySelector('[data-meerbegleiter-property-fitting-size]'),
      fittingFinish: root.querySelector('[data-meerbegleiter-property-fitting-finish]'),
      extras: root.querySelector('[data-meerbegleiter-property-extras]'),
      extrasFinish: root.querySelector('[data-meerbegleiter-property-extras-finish]'),
      extrasCount: root.querySelector('[data-meerbegleiter-property-extras-count]'),
      engravingArea: root.querySelector('[data-meerbegleiter-property-engraving-area]'),
      mode: root.querySelector('[data-meerbegleiter-property-mode]'),
      latitude: root.querySelector('[data-meerbegleiter-property-latitude]'),
      longitude: root.querySelector('[data-meerbegleiter-property-longitude]')
    };

    const modeInputs = Array.from(root.querySelectorAll('[data-meerbegleiter-mode]'));
    const panels = Array.from(root.querySelectorAll('[data-meerbegleiter-panel]'));
    const engravingChoiceInputs = Array.from(root.querySelectorAll('[data-meerbegleiter-engraving-choice]'));
    const extrasChoiceInputs = Array.from(root.querySelectorAll('[data-meerbegleiter-extras-choice]'));
    const extrasFinishInputs = Array.from(root.querySelectorAll('[data-meerbegleiter-extras-finish]'));
    let pendingRopeDiameterMm = null;
    let ropeModalLastFocus = null;
    let extrasModalLastFocus = null;
    let nextExtraTokenId = 1;
    let cartRequestInFlight = false;

    const selectedModel = () => catalog.models.find((model) => model.id === state.modelId) || null;
    const selectedRope = () => catalog.ropes.find((rope) => rope.id === state.ropeId) || null;
    const selectedFitting = () => catalog.fittings.find((fitting) => fitting.id === state.fittingId) || null;
    const fittingDisplayName = (fitting) => fitting?.displayName || fitting?.name || fitting?.finish || '';
    const realImageUrl = (entry) => shopifyDraft?.realCatalogImageUrl?.(entry) || null;
    const approvedEngravingProfile = (fitting) => fitting && shopifyDraft?.resolveApprovedEngravingProfile?.(
      fitting.engravingProfileId,
      fitting.id,
      shopifyDraftConfig
    );
    const selectedModelSupportsEngraving = () => selectedModel()?.engravingAvailable === true;
    const extrasAvailable = () => Boolean(
      selectedModel()
      && Number.isFinite(Number(state.diameterMm))
      && (shopifyDraft?.extrasAllowedForDiameter
        ? shopifyDraft.extrasAllowedForDiameter(state.diameterMm, shopifyDraftConfig)
        : catalog.extrasConfig.availableDiameters.includes(Number(state.diameterMm)))
    );
    const extrasEnabled = () => extrasAvailable() && state.extrasEnabled;
    const extrasCombination = () => state.extras.map((token) => token.value).join('');
    const extrasCount = () => shopifyDraft?.countPhysicalExtraElements
      ? shopifyDraft.countPhysicalExtraElements(extrasCombination())
      : Array.from(extrasCombination().replace(/\s+/gu, '')).length;
    const extrasFinishName = () => catalog.extrasConfig.finishes
      .find((finish) => finish.id === state.extrasFinish)?.name || '';
    const physicalExtraStateTokens = () => state.extras.filter((token) => token.physical !== false);
    const catalogExtraItemFor = (type, value, finish = state.extrasFinish) => catalog.extrasConfig.items.find((item) => (
      item.type === type
      && item.token === value
      && item.finish === finish
      && item.availableDiameters?.includes(Number(state.diameterMm))
    )) || null;
    const extraAvailabilityFor = (item, requiredQuantity = 1) => shopifyDraft?.extraVariantAvailability
      ? shopifyDraft.extraVariantAvailability(item, requiredQuantity)
      : { available: false, reason: 'inventory-unavailable', requiredQuantity, inventoryQuantity: null };
    const extraInventoryShortfallForItems = (items) => {
      const grouped = new Map();
      for (const item of items) {
        if (!item?.shopifyVariantId) {
          return { item, quantity: 1, availability: extraAvailabilityFor(item) };
        }
        const current = grouped.get(item.shopifyVariantId);
        if (current) current.quantity += 1;
        else grouped.set(item.shopifyVariantId, { item, quantity: 1 });
      }
      for (const entry of grouped.values()) {
        const availability = extraAvailabilityFor(entry.item, entry.quantity);
        if (!availability.available) return { ...entry, availability };
      }
      return null;
    };
    const selectedExtraCatalogItems = () => physicalExtraStateTokens().map((token) => (
      catalog.extrasConfig.items.find((item) => item.id === token.itemId && item.shopifyVariantId === token.shopifyVariantId) || null
    ));
    const extrasInventoryShortfall = () => extraInventoryShortfallForItems(selectedExtraCatalogItems());
    const extrasTotalCents = () => extrasEnabled() && !extrasInventoryShortfall()
      ? physicalExtraStateTokens().reduce((total, token) => total + (Number(token.unitPriceCents) || 0), 0)
      : 0;
    const extraInventoryMessage = (shortfall) => {
      const label = shortfall?.item?.label || shortfall?.item?.elementName || shortfall?.item?.token || 'Dieses Extraelement';
      const finish = shortfall?.item?.finishName || extrasFinishName();
      if (shortfall?.availability?.reason === 'insufficient-stock') {
        return `${label} in ${finish} wird ${shortfall.quantity}-mal benötigt, ist aber nur ${shortfall.availability.inventoryQuantity}-mal verfügbar.`;
      }
      if (shortfall?.availability?.reason === 'sold-out') return `${label} in ${finish} ist aktuell ausverkauft.`;
      return `Der aktuelle Shopify-Bestand für ${label}${finish ? ` in ${finish}` : ''} ist nicht sicher verfügbar.`;
    };
    const basePriceCents = () => {
      const price = resolveShopifyConfiguration()?.priceCents;
      return Number.isInteger(price) ? price : null;
    };
    const fittingSurchargeCents = () => Number.isInteger(selectedFitting()?.priceCents)
      ? selectedFitting().priceCents
      : 0;
    const calculateTotalCents = () => {
      const base = basePriceCents();
      if (base === null) return null;
      if (!shopifyDraft?.calculateTotalCents) return base + fittingSurchargeCents() + extrasTotalCents();
      return shopifyDraft.calculateTotalCents({
        basePriceCents: base,
        extrasPriceCents: extrasEnabled() ? extrasTotalCents() : 0,
        fittingSurchargeCents: fittingSurchargeCents()
      });
    };
    const availableDiametersForModel = (modelId = state.modelId) => {
      const model = catalog.models.find((entry) => entry.id === modelId) || null;
      const allowedDiameters = new Set((model?.availableDiameters || []).map(Number));
      return catalog.ropeDiameters
        .filter((diameter) => diameter.active && allowedDiameters.has(Number(diameter.diameterMm)))
        .map((diameter) => Number(diameter.diameterMm));
    };
    const isDiameterCompatible = (diameterMm, modelId = state.modelId) => (
      availableDiametersForModel(modelId).includes(Number(diameterMm))
    );
    const isRopeCompatible = (rope, modelId = state.modelId, diameterMm = state.diameterMm) => Boolean(
      rope
      && rope.active
      && rope.compatibleModelIds.includes(modelId)
      && Number(rope.diameterMm) === Number(diameterMm)
    );
    const compatibleRopesFor = (modelId = state.modelId, diameterMm = state.diameterMm) => catalog.ropes
      .filter((rope) => isRopeCompatible(rope, modelId, diameterMm))
      .sort((first, second) => Number(first.sortOrder || 0) - Number(second.sortOrder || 0));

    const setProperty = (input, value) => {
      if (!input) return;
      const normalized = value === null || value === undefined ? '' : String(value);
      input.value = normalized;
      input.disabled = normalized.length === 0;
    };

    const setFieldError = (name, message = '') => {
      const error = root.querySelector(`[data-meerbegleiter-error="${name}"]`);
      const fields = name === 'rope' ? [elements.rope]
        : name === 'text' ? [elements.text]
          : name === 'place' ? [elements.place]
            : name === 'latitude' ? [elements.latitudeDegrees, elements.latitudeMinutes, elements.latitudeDirection]
              : name === 'longitude' ? [elements.longitudeDegrees, elements.longitudeMinutes, elements.longitudeDirection]
                : name === 'confirmation' ? [elements.confirmation]
                  : [];
      if (error) error.textContent = message;
      fields.filter(Boolean).forEach((field) => {
        if (message) field.setAttribute('aria-invalid', 'true');
        else field.removeAttribute('aria-invalid');
      });
    };

    const clearErrors = () => {
      ['diameter', 'rope', 'fitting', 'text', 'latitude', 'longitude', 'place', 'confirmation'].forEach((name) => setFieldError(name));
      elements.formError.textContent = '';
      elements.formError.hidden = true;
    };

    const isFittingCompatible = (fitting, modelId = state.modelId, diameterMm = state.diameterMm) => Boolean(
      fitting
      && fitting.active
      && fitting.compatibleModelIds.includes(modelId)
      && fitting.compatibleDiameters.includes(Number(diameterMm))
    );

    const compatibleFittings = () => catalog.fittings.filter((fitting) => isFittingCompatible(fitting));
    const usesAutomaticClassicFitting = () => {
      const diameters = availableDiametersForModel();
      return state.modelId === 'classic' && diameters.length === 1 && diameters[0] === 8;
    };

    const createChoice = ({ groupName, value, title, description, meta, imageUrl, showMedia = false, checked, disabled, onChange, onClick }) => {
      const label = document.createElement('label');
      label.className = 'suedlis-meerbegleiter-personalizer__choice';
      label.dataset.meerbegleiterChoiceCard = '';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = groupName;
      input.value = String(value);
      input.checked = Boolean(checked);
      input.disabled = Boolean(disabled);
      input.addEventListener('change', () => {
        if (!input.checked) return;
        onChange(input.value);
      });
      if (typeof onClick === 'function') {
        label.addEventListener('click', () => {
          if (input.disabled) return;
          onClick(String(value), input);
        });
      }

      const surface = document.createElement('span');
      if (showMedia) {
        const media = document.createElement('span');
        media.className = 'suedlis-meerbegleiter-personalizer__choice-media';
        media.setAttribute('aria-hidden', 'true');
        if (typeof imageUrl === 'string' && imageUrl.trim()) {
          const image = document.createElement('img');
          image.src = imageUrl;
          image.alt = '';
          image.loading = 'lazy';
          image.decoding = 'async';
          image.addEventListener('error', () => {
            image.remove();
            media.classList.remove('has-image');
          }, { once: true });
          media.appendChild(image);
          media.classList.add('has-image');
        }
        surface.appendChild(media);
      }
      const heading = document.createElement('strong');
      heading.textContent = title;
      surface.appendChild(heading);

      if (description) {
        const copy = document.createElement('small');
        copy.textContent = description;
        surface.appendChild(copy);
      }
      if (meta) {
        const detail = document.createElement('b');
        detail.textContent = meta;
        surface.appendChild(detail);
      }

      label.append(input, surface);
      label.classList.toggle('is-active', input.checked);
      label.classList.toggle('is-disabled', input.disabled);
      return label;
    };

    const createUnavailableChoice = () => {
      const item = document.createElement('div');
      item.className = 'suedlis-meerbegleiter-personalizer__choice-empty';
      const media = document.createElement('span');
      media.className = 'suedlis-meerbegleiter-personalizer__choice-media';
      media.setAttribute('aria-hidden', 'true');
      const copy = document.createElement('p');
      copy.textContent = 'Diese Auswahl ist aktuell noch nicht verfügbar.';
      item.append(media, copy);
      return item;
    };

    const refreshChoiceStates = () => {
      root.querySelectorAll('[data-meerbegleiter-choice-card]').forEach((card) => {
        const input = card.querySelector('input');
        card.classList.toggle('is-active', Boolean(input?.checked));
        card.classList.toggle('is-disabled', Boolean(input?.disabled));
      });
      root.querySelectorAll('[data-meerbegleiter-mode-card]').forEach((card) => {
        const input = card.querySelector('[data-meerbegleiter-mode]');
        card.classList.toggle('is-active', Boolean(input?.checked));
        card.classList.toggle('is-disabled', Boolean(input?.disabled));
        card.setAttribute('aria-disabled', input?.disabled ? 'true' : 'false');
      });
      root.querySelectorAll('[data-meerbegleiter-optional-card]').forEach((card) => {
        const input = card.querySelector('input[type="radio"]');
        card.classList.toggle('is-active', Boolean(input?.checked));
        card.classList.toggle('is-disabled', Boolean(input?.disabled));
        card.setAttribute('aria-disabled', input?.disabled ? 'true' : 'false');
      });
      root.querySelectorAll('[data-meerbegleiter-rope-modal-card]').forEach((card) => {
        const active = card.dataset.meerbegleiterRopeId === state.ropeId;
        card.classList.toggle('is-active', active);
        card.setAttribute('aria-pressed', active ? 'true' : 'false');
        const badge = card.querySelector('[data-meerbegleiter-rope-modal-badge]');
        if (badge) badge.textContent = active ? 'Ausgewählt' : 'Auswählen';
      });
    };

    const renderDiameters = () => {
      const allowedDiameters = availableDiametersForModel();
      const cards = catalog.ropeDiameters
        .filter((diameter) => allowedDiameters.includes(Number(diameter.diameterMm)))
        .map((diameter) => createChoice({
          groupName: 'meerbegleiter-diameter',
          value: diameter.diameterMm,
          title: `${diameter.diameterMm} mm`,
          description: diameter.description,
          imageUrl: diameter.imageUrl,
          showMedia: true,
          checked: Number(state.diameterMm) === diameter.diameterMm,
          disabled: false,
          onChange: () => {},
          onClick: (value) => {
            const nextDiameter = Number(value);
            clearErrors();
            openRopeModal(nextDiameter);
            renderDiameters();
          }
        }));
      elements.diameterOptions.replaceChildren(...cards);
    };

    const createRopeModalCard = (rope) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'suedlis-meerbegleiter-personalizer__rope-modal-card';
      button.dataset.meerbegleiterRopeModalCard = '';
      button.dataset.meerbegleiterRopeId = rope.id;
      button.setAttribute('aria-pressed', state.ropeId === rope.id ? 'true' : 'false');

      const media = document.createElement('span');
      media.className = 'suedlis-meerbegleiter-personalizer__rope-modal-card-media';
      const ropeImageUrl = realImageUrl(rope);
      if (ropeImageUrl) {
        const image = document.createElement('img');
        image.src = ropeImageUrl;
        image.alt = rope.name;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.addEventListener('error', () => {
          image.remove();
          media.classList.remove('has-image');
        }, { once: true });
        media.appendChild(image);
        media.classList.add('has-image');
      }

      const body = document.createElement('span');
      body.className = 'suedlis-meerbegleiter-personalizer__rope-modal-card-body';
      const title = document.createElement('strong');
      title.textContent = rope.name;
      const detail = document.createElement('small');
      detail.textContent = [rope.colorCombination || rope.color, `${rope.diameterMm} mm`, rope.material, rope.testOnly ? 'Testdarstellung' : ''].filter(Boolean).join(' · ');
      const badge = document.createElement('b');
      badge.dataset.meerbegleiterRopeModalBadge = '';
      badge.textContent = state.ropeId === rope.id ? 'Ausgewählt' : 'Auswählen';
      body.append(title, detail, badge);

      button.append(media, body);
      button.classList.toggle('is-active', state.ropeId === rope.id);
      button.addEventListener('click', () => selectRopeFromModal(rope.id));
      return button;
    };

    const renderRopeModalOptions = () => {
      const diameterMm = Number(pendingRopeDiameterMm);
      const model = selectedModel();
      const matches = model && isDiameterCompatible(diameterMm)
        ? compatibleRopesFor(model.id, diameterMm)
        : [];

      if (elements.ropeModalTitle) elements.ropeModalTitle.textContent = 'Tau-Farbe auswählen';
      if (elements.ropeModalDiameter) elements.ropeModalDiameter.textContent = Number.isFinite(diameterMm) ? `${diameterMm} mm` : '';

      if (matches.length > 0) {
        elements.ropeOptions.replaceChildren(...matches.map(createRopeModalCard));
        elements.ropeModalStatus.textContent = `${matches.length} verfügbare ${diameterMm}-mm-Farbe${matches.length === 1 ? '' : 'n'} für diese Ausführung.`;
      } else {
        elements.ropeOptions.replaceChildren(createUnavailableChoice());
        elements.ropeModalStatus.textContent = Number.isFinite(diameterMm)
          ? `Für ${diameterMm} mm sind aktuell keine Tau-Farben in den vorhandenen Daten freigegeben.`
          : 'Wähle zuerst eine verfügbare Tau-Stärke.';
      }
      refreshChoiceStates();
    };

    const openRopeModal = (diameterMm = state.diameterMm) => {
      const nextDiameter = Number(diameterMm);
      if (!elements.ropeModal || !Number.isFinite(nextDiameter) || !isDiameterCompatible(nextDiameter)) return;
      pendingRopeDiameterMm = nextDiameter;
      ropeModalLastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      renderRopeModalOptions();
      elements.ropeModal.hidden = false;
      elements.ropeModal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('suedlis-meerbegleiter-rope-modal-open');
      window.requestAnimationFrame(() => {
        const activeCard = elements.ropeModal.querySelector('.suedlis-meerbegleiter-personalizer__rope-modal-card.is-active');
        const closeButton = elements.ropeModal.querySelector('[data-meerbegleiter-rope-modal-close]:not(.suedlis-meerbegleiter-personalizer__rope-modal-backdrop)');
        (activeCard || closeButton)?.focus();
      });
    };

    const closeRopeModal = ({ restoreFocus = true } = {}) => {
      if (!elements.ropeModal) return;
      elements.ropeModal.hidden = true;
      elements.ropeModal.setAttribute('aria-hidden', 'true');
      pendingRopeDiameterMm = null;
      document.body.classList.remove('suedlis-meerbegleiter-rope-modal-open');
      if (restoreFocus) ropeModalLastFocus?.focus();
      ropeModalLastFocus = null;
      renderDiameters();
    };

    const selectRopeFromModal = (ropeId) => {
      const rope = catalog.ropes.find((entry) => entry.id === ropeId) || null;
      if (!rope || !isDiameterCompatible(rope.diameterMm) || !isRopeCompatible(rope, state.modelId, rope.diameterMm)) return;
      const nextDiameter = Number(rope.diameterMm);
      const previousFitting = selectedFitting();
      state.diameterMm = nextDiameter;
      state.ropeId = rope.id;
      if (!extrasAvailable()) resetExtrasState();
      elements.rope.value = rope.id;
      if (!isFittingCompatible(previousFitting, state.modelId, nextDiameter)) state.fittingId = '';
      clearErrors();
      renderDiameters();
      renderRopes();
      renderFittings(Boolean(previousFitting));
      updateAll();
      closeRopeModal({ restoreFocus: false });
    };

    const renderRopes = () => {
      const model = selectedModel();
      const matches = compatibleRopesFor();

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = matches.length > 0 ? 'Bitte Tauausführung wählen' : 'Diese Auswahl ist aktuell noch nicht verfügbar.';
      elements.rope.replaceChildren(placeholder);

      matches.forEach((rope) => {
        const option = document.createElement('option');
        option.value = rope.id;
        option.textContent = rope.name;
        elements.rope.appendChild(option);
      });

      elements.rope.disabled = matches.length === 0;
      elements.rope.value = matches.some((rope) => rope.id === state.ropeId) ? state.ropeId : '';
      if (!elements.rope.value) state.ropeId = '';

      const selected = selectedRope();
      elements.ropeSelection?.classList.toggle('has-selection', Boolean(selected));
      if (elements.ropeSelection) elements.ropeSelection.hidden = !selected;
      if (elements.ropeSelectionName) elements.ropeSelectionName.textContent = selected?.name || '';
      if (elements.ropeSelectionMeta) {
        elements.ropeSelectionMeta.textContent = selected
          ? [`${selected.diameterMm} mm`, selected.colorCombination || selected.color, selected.material, selected.testOnly ? 'Testdarstellung' : ''].filter(Boolean).join(' · ')
          : '';
      }
      if (elements.ropeSelectionImage && elements.ropeSelectionMedia) {
        const selectedImageUrl = realImageUrl(selected);
        if (selectedImageUrl) {
          elements.ropeSelectionImage.onerror = () => {
            elements.ropeSelectionImage.removeAttribute('src');
            elements.ropeSelectionImage.alt = '';
            elements.ropeSelectionMedia.hidden = true;
          };
          elements.ropeSelectionImage.src = selectedImageUrl;
          elements.ropeSelectionImage.alt = selected.name;
          elements.ropeSelectionMedia.hidden = false;
        } else {
          elements.ropeSelectionImage.removeAttribute('src');
          elements.ropeSelectionImage.alt = '';
          elements.ropeSelectionImage.onerror = null;
          elements.ropeSelectionMedia.hidden = true;
        }
      }
      if (elements.ropeModalTrigger) {
        elements.ropeModalTrigger.disabled = !state.diameterMm || !isDiameterCompatible(state.diameterMm);
        elements.ropeModalTrigger.textContent = selected ? 'Farbe wechseln' : 'Farbe wählen';
      }

      if (!model) elements.ropeStatus.textContent = 'Wähle zuerst dein Modell und deine Tau-Stärke.';
      else if (!state.diameterMm) elements.ropeStatus.textContent = 'Wähle eine sichtbare Tau-Stärke, um die passenden Farben zu öffnen.';
      else if (matches.length === 0) elements.ropeStatus.textContent = 'Diese Auswahl ist aktuell noch nicht verfügbar.';
      else if (selected) elements.ropeStatus.textContent = `Ausgewählt: ${state.diameterMm} mm · ${selected.name}`;
      else elements.ropeStatus.textContent = `Klicke auf ${state.diameterMm} mm, um deine Farbkombination auszuwählen.`;

      if (pendingRopeDiameterMm) renderRopeModalOptions();
    };

    const fittingStatusMessage = (hadInvalidFitting = false) => {
      const model = selectedModel();
      if (!model) return 'Wähle zuerst das Meerbegleiter-Modell.';
      if (!state.diameterMm) return 'Wähle zuerst die Tau-Stärke.';

      const matches = compatibleFittings();
      if (matches.length === 0) return 'Für diese Kombination ist noch kein Endstück hinterlegt.';
      const needsCenterpiece = model.requiredFittingTypes.includes('centerpiece');
      if (hadInvalidFitting || !state.fittingId) return needsCenterpiece
        ? `Bitte wähle ein passendes Mittelstück für dein ${state.diameterMm}-mm-Tau.`
        : '';
      return needsCenterpiece ? 'Dein Mittelstück passt zu deiner Auswahl.' : '';
    };

    const updateFittingStatus = (hadInvalidFitting = false) => {
      if (!elements.fittingStatus) return;
      const message = fittingStatusMessage(hadInvalidFitting);
      elements.fittingStatus.textContent = message;
      elements.fittingStatus.hidden = !message;
    };

    const renderFittings = (hadInvalidFitting = false) => {
      const onlyClassicEight = usesAutomaticClassicFitting();
      const matches = compatibleFittings()
        .sort((first, second) => Number(first.sortOrder || 0) - Number(second.sortOrder || 0));
      const automaticFitting = onlyClassicEight && Number(state.diameterMm) === 8 && matches.length === 1
        && matches[0].id === 'endcap-stainless-matte-8' && matches[0].type === 'endcap' && !matches[0].testOnly
        && matches[0].engravingApprovalStatus === 'production-approved' && approvedEngravingProfile(matches[0])
        ? matches[0] : null;
      if (onlyClassicEight) state.fittingId = automaticFitting?.id || '';
      else if (!matches.some((fitting) => fitting.id === state.fittingId)) state.fittingId = '';
      const compactEndcapCards = [6, 8].includes(Number(state.diameterMm));
      const cards = matches.map((fitting) => createChoice({
        groupName: 'meerbegleiter-fitting',
        value: fitting.id,
        title: fittingDisplayName(fitting),
        description: compactEndcapCards && fitting.type === 'endcap' ? '' : `für ${state.diameterMm}-mm-Tau`,
        meta: compactEndcapCards && fitting.type === 'endcap' ? (fitting.testOnly ? 'Testdarstellung · nicht bestellbar' : '') : [
          fitting.testOnly ? 'Testdarstellung · nicht bestellbar' : '',
          previewTestDataEnabled && selectedModelSupportsEngraving() && (fitting.engraving || catalog.fittings.find((entry) => entry.engravable && entry.engraving)?.engraving)
            ? (() => {
              const engraving = fitting.engraving || catalog.fittings.find((entry) => entry.engravable && entry.engraving).engraving;
              return `Für Gravur geeignet · ${engraving.widthMm} × ${engraving.heightMm} mm`;
            })()
            : fitting.engravable && fitting.engraving
            ? `Für Gravur geeignet · ${fitting.engraving.widthMm} × ${fitting.engraving.heightMm} mm`
            : fitting.engravable === false ? 'Ohne Gravur' : ''
        ].filter(Boolean).join(' · '),
        imageUrl: realImageUrl(fitting),
        showMedia: true,
        checked: state.fittingId === fitting.id,
        disabled: onlyClassicEight,
        onChange: (fittingId) => {
          if (onlyClassicEight) return;
          state.fittingId = fittingId;
          clearErrors();
          updateFittingStatus();
          updateAll();
        }
      }));
      elements.fittingOptions.replaceChildren(...cards);
      updateFittingStatus(hadInvalidFitting);
    };

    const approvedTestEngraving = catalog.fittings.find((fitting) => fitting.id === 'endcap-stainless-polished-6')?.engraving || null;

    const availableEngraving = () => {
      if (!selectedModelSupportsEngraving()) return null;
      const fitting = selectedFitting();
      if (fitting?.engravable && fitting.engraving && approvedEngravingProfile(fitting)) return fitting.engraving;
      if (previewTestDataEnabled && approvedTestEngraving) {
        return { ...approvedTestEngraving, position: 'Test-Gravurfläche', testOnly: true };
      }
      return null;
    };
    const currentEngraving = () => state.engravingEnabled ? availableEngraving() : null;

    const engravingModeLabel = (mode = state.engravingMode) => ({
      text: 'Eigener Text',
      coordinates: 'Koordinaten'
    }[mode] || 'Eigener Text');

    const enforceMaxLength = (field, limit) => {
      if (!field || !Number.isFinite(limit) || limit < 1 || field.value.length <= limit) return;
      field.value = field.value.slice(0, limit);
    };

    const coordinateAxes = {
      latitude: {
        label: 'Breitengrad',
        degrees: elements.latitudeDegrees,
        minutes: elements.latitudeMinutes,
        direction: elements.latitudeDirection,
        maxDegrees: 90,
        directions: ['N', 'S']
      },
      longitude: {
        label: 'Längengrad',
        degrees: elements.longitudeDegrees,
        minutes: elements.longitudeMinutes,
        direction: elements.longitudeDirection,
        maxDegrees: 180,
        directions: ['O', 'W']
      }
    };

    const coordinateControls = Object.values(coordinateAxes)
      .flatMap((axis) => [axis.degrees, axis.minutes, axis.direction])
      .filter(Boolean);

    const resetEngravingState = () => {
      state.engravingEnabled = false;
      state.engravingMode = 'text';
      engravingChoiceInputs.forEach((input) => { input.checked = input.value === 'none'; });
      modeInputs.forEach((input) => { input.checked = input.value === 'text'; });
      elements.text.value = '';
      elements.place.value = '';
      coordinateControls.forEach((control) => {
        if (control instanceof HTMLSelectElement) control.selectedIndex = 0;
        else control.value = '';
      });
      elements.confirmation.checked = false;
    };

    const resetExtrasState = () => {
      state.extrasEnabled = false;
      state.extrasFinish = '';
      state.extras = [];
      extrasChoiceInputs.forEach((input) => { input.checked = input.value === 'none'; });
      extrasFinishInputs.forEach((input) => { input.checked = false; });
      if (elements.extrasInput) elements.extrasInput.value = '';
      if (elements.extrasError) elements.extrasError.textContent = '';
    };

    const coordinateValue = (axisName) => {
      const axis = coordinateAxes[axisName];
      if (!axis || axis.degrees.value === '' || axis.minutes.value === '') return '';
      const degrees = Number(axis.degrees.value);
      const minutes = Number(axis.minutes.value);
      const direction = axis.direction.value;
      if (
        !Number.isInteger(degrees)
        || !Number.isInteger(minutes)
        || degrees < 0
        || degrees > axis.maxDegrees
        || minutes < 0
        || minutes > 59
        || (degrees === axis.maxDegrees && minutes !== 0)
        || !axis.directions.includes(direction)
      ) return '';
      return `${degrees}°${minutes}′${direction}`;
    };

    const validateCoordinateAxis = (axisName, showError = false) => {
      const axis = coordinateAxes[axisName];
      let message = '';
      if (axis.degrees.value === '' || axis.minutes.value === '') {
        message = `Bitte Grad und Minuten für den ${axis.label} vollständig eingeben.`;
      } else {
        const degrees = Number(axis.degrees.value);
        const minutes = Number(axis.minutes.value);
        if (!Number.isInteger(degrees) || !Number.isInteger(minutes)) message = 'Bitte nur ganze Grad- und Minutenwerte eingeben.';
        else if (degrees < 0 || degrees > axis.maxDegrees) message = `${axis.label}: Grad müssen zwischen 0 und ${axis.maxDegrees} liegen.`;
        else if (minutes < 0 || minutes > 59) message = `${axis.label}: Minuten müssen zwischen 0 und 59 liegen.`;
        else if (degrees === axis.maxDegrees && minutes !== 0) message = `${axis.label}: Bei ${axis.maxDegrees} Grad müssen die Minuten 0 sein.`;
        else if (!axis.directions.includes(axis.direction.value)) message = `Bitte eine gültige Himmelsrichtung für den ${axis.label} wählen.`;
      }
      if (showError) setFieldError(axisName, message);
      return message.length === 0;
    };

    const coordinateEngraving = () => ({
      latitude: coordinateValue('latitude'),
      longitude: coordinateValue('longitude'),
      place: elements.place.value
    });

    const splitTextForPreview = (value, maxLines) => {
      if (!value) return [];
      if (maxLines < 2 || Array.from(value).length <= 8) return [value];
      const words = value.trim().split(/\s+/u);
      if (words.length < 2) return [value];
      const context = document.createElement('canvas').getContext('2d');
      if (context) context.font = '600 100px Montserrat, Arial, sans-serif';
      const measure = (text) => context ? context.measureText(text).width : Array.from(text).length;
      const candidates = [];
      for (let index = 1; index < words.length; index += 1) {
        const lines = [words.slice(0, index).join(' '), words.slice(index).join(' ')];
        candidates.push({
          lines,
          widest: Math.max(...lines.map(measure)),
          balance: Math.abs(Array.from(lines[0]).length - Array.from(lines[1]).length)
        });
      }
      candidates.sort((first, second) => first.widest - second.widest || first.balance - second.balance);
      return candidates[0]?.lines || [value];
    };

    const renderPreviewLines = (lines) => {
      const nodes = lines.map(({ text, placeholder = false }) => {
        const line = document.createElement('span');
        line.className = 'suedlis-meerbegleiter-personalizer__preview-line';
        line.classList.toggle('is-placeholder-line', placeholder);
        line.textContent = text;
        return line;
      });
      elements.previewText.replaceChildren(...nodes);
      elements.previewText.classList.toggle('is-placeholder', lines.every((line) => line.placeholder));
    };

    let previewFittingImage = null;
    let previewImageUrl = '';
    let failedPreviewImageUrl = '';
    let previewImageRequestId = 0;
    const fittingPreviewImageUrl = () => {
      const fitting = selectedFitting();
      return currentEngraving() && fitting?.id === 'endcap-stainless-matte-8'
        ? realImageUrl(fitting) : null;
    };

    const updateFittingPreviewImage = () => {
      if (!elements.previewSurface) return;
      const imageUrl = fittingPreviewImageUrl();
      if (!imageUrl || imageUrl === failedPreviewImageUrl) {
        previewImageRequestId += 1;
        previewImageUrl = '';
        elements.previewSurface.classList.remove('has-fitting-image');
        if (previewFittingImage) previewFittingImage.hidden = true;
        return;
      }
      if (previewImageUrl === imageUrl) return;
      if (!previewFittingImage) {
        previewFittingImage = document.createElement('img');
        previewFittingImage.className = 'suedlis-meerbegleiter-personalizer__preview-fitting-image';
        previewFittingImage.width = 1200;
        previewFittingImage.height = 1200;
        previewFittingImage.alt = '';
        previewFittingImage.hidden = true;
        elements.previewSurface.prepend(previewFittingImage);
      }
      previewImageUrl = imageUrl;
      const requestId = ++previewImageRequestId;
      const isCurrentImage = () => requestId === previewImageRequestId
        && previewImageUrl === imageUrl && fittingPreviewImageUrl() === imageUrl;
      previewFittingImage.onload = () => {
        if (!isCurrentImage()) return;
        previewFittingImage.hidden = false;
        elements.previewSurface.classList.add('has-fitting-image');
        fitPreview();
      };
      previewFittingImage.onerror = () => {
        if (!isCurrentImage()) return;
        failedPreviewImageUrl = imageUrl;
        previewImageUrl = '';
        previewImageRequestId += 1;
        previewFittingImage.hidden = true;
        elements.previewSurface.classList.remove('has-fitting-image');
        fitPreview();
      };
      if (previewFittingImage.getAttribute('src') !== imageUrl) previewFittingImage.src = imageUrl;
      if (previewFittingImage.complete && previewFittingImage.naturalWidth > 0) previewFittingImage.onload();
    };

    const fitPreview = () => {
      if (!elements.previewText || elements.preview.hidden) return;
      const lines = Array.from(elements.previewText.querySelectorAll('.suedlis-meerbegleiter-personalizer__preview-line'));
      lines.forEach((line) => line.style.removeProperty('transform'));
      const fittingImageVisible = elements.previewSurface.classList.contains('has-fitting-image');
      const isPhotoTextPlaceholder = fittingImageVisible && state.engravingMode === 'text' && elements.previewText.classList.contains('is-placeholder');
      const engraving = currentEngraving();
      const minimumFontSize = fittingImageVisible ? 10 : Number(engraving?.minPreviewFontPx) || 16;
      let fontSize = isPhotoTextPlaceholder ? 14 : fittingImageVisible
        ? state.engravingMode === 'coordinates' ? 18 : 22
        : state.engravingMode === 'coordinates' ? 24 : 28;
      elements.previewText.style.fontSize = `${fontSize}px`;
      while (
        fontSize > minimumFontSize
        && (elements.previewText.scrollWidth > elements.previewText.clientWidth || elements.previewText.scrollHeight > elements.previewText.clientHeight)
      ) {
        fontSize -= 1;
        elements.previewText.style.fontSize = `${fontSize}px`;
      }
      if (fittingImageVisible && elements.previewText.clientWidth > 0) {
        const availableWidth = elements.previewText.clientWidth;
        lines.forEach((line) => {
          if (line.scrollWidth > availableWidth) line.style.transform = `scaleX(${availableWidth / line.scrollWidth})`;
        });
      }
    };

    if (elements.previewSurface && typeof window.ResizeObserver === 'function') {
      const previewResizeObserver = new window.ResizeObserver(fitPreview);
      previewResizeObserver.observe(elements.previewSurface);
    } else if (typeof window.addEventListener === 'function') {
      window.addEventListener('resize', fitPreview);
    }

    const updatePreview = () => {
      const engraving = currentEngraving();
      updateFittingPreviewImage();
      elements.preview.hidden = !engraving;
      if (elements.previewStep) elements.previewStep.hidden = !engraving;
      if (!engraving) return;

      elements.previewPosition.textContent = engraving.position;
      elements.previewSurface.style.aspectRatio = `${engraving.widthMm} / ${engraving.heightMm}`;
      elements.previewText.hidden = false;

      elements.previewText.classList.toggle('is-coordinates', state.engravingMode === 'coordinates');
      if (state.engravingMode === 'coordinates') {
        const coordinates = coordinateEngraving();
        const coordinateLines = [
          { text: coordinates.latitude || 'Breitengrad', placeholder: !coordinates.latitude },
          { text: coordinates.longitude || 'Längengrad', placeholder: !coordinates.longitude }
        ];
        if (coordinates.place.trim()) coordinateLines.push({ text: coordinates.place });
        renderPreviewLines(coordinateLines.slice(0, Number(engraving.coordinateMaxLines) || 3));
      } else {
        const textLines = splitTextForPreview(elements.text.value, Number(engraving.textMaxLines) || 2);
        renderPreviewLines(textLines.length > 0
          ? textLines.map((line) => ({ text: line }))
          : [{ text: 'Deine Gravur', placeholder: true }]);
      }
      window.requestAnimationFrame(fitPreview);
    };

    const updateEngravingAvailability = () => {
      const engraving = availableEngraving();
      const available = Boolean(engraving);
      if (!available && state.engravingEnabled) resetEngravingState();
      const active = available && state.engravingEnabled;
      elements.withdrawalNotice.hidden = !active;
      elements.personalizationStep.hidden = !available;
      elements.engravingControls.hidden = !active;
      elements.engravingStep.hidden = !active;
      engravingChoiceInputs.forEach((input) => {
        input.disabled = !available;
        input.checked = input.value === (active ? 'add' : 'none');
      });
      modeInputs.forEach((input) => { input.disabled = !active; });

      const coordinatesActive = active && state.engravingMode === 'coordinates';
      const textActive = active && state.engravingMode === 'text';
      panels.forEach((panel) => { panel.hidden = !active || panel.dataset.meerbegleiterPanel !== state.engravingMode; });
      elements.detailsTitle.textContent = coordinatesActive ? 'Koordinaten und Ort' : 'Dein Gravurtext';

      const textLimit = active ? Number(engraving.maxCharacters) : 0;
      const placeLimit = active ? Number(engraving.placeMaxCharacters) : 0;
      if (active) {
        elements.text.maxLength = textLimit;
        elements.place.maxLength = placeLimit;
        enforceMaxLength(elements.text, textLimit);
        enforceMaxLength(elements.place, placeLimit);
      } else {
        elements.text.removeAttribute('maxlength');
        elements.place.removeAttribute('maxlength');
      }
      elements.textLimit.textContent = active ? String(textLimit) : '–';
      elements.placeLimit.textContent = active ? String(placeLimit) : '–';

      elements.text.disabled = !textActive;
      elements.text.required = textActive;
      elements.place.disabled = !coordinatesActive;
      elements.place.required = false;
      coordinateControls.forEach((control) => {
        control.disabled = !coordinatesActive;
        control.required = coordinatesActive;
      });
      elements.confirmation.disabled = !coordinatesActive;
      elements.confirmation.required = coordinatesActive;
      setProperty(properties.mode, active ? engravingModeLabel() : '');
      refreshChoiceStates();
      updatePreview();
    };

    const resolveShopifyConfiguration = () => {
      if (!selectedModel() || !isDiameterCompatible(state.diameterMm)) return null;
      return shopifyDraft?.resolveBaseVariant
        ? shopifyDraft.resolveBaseVariant(state.modelId, state.diameterMm, shopifyDraftConfig)
        : catalog.shopifyConfigurations.find((configuration) => Boolean(
          configuration.active
          && configuration.modelId === state.modelId
          && Number(configuration.diameterMm) === Number(state.diameterMm)
        )) || null;
    };

    const formatPrice = (cents) => new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: catalog.currency || 'EUR'
    }).format(Number(cents) / 100);

    const visibleStepConfig = [
      { id: 'rope', visible: () => true },
      { id: 'fitting', visible: () => !usesAutomaticClassicFitting() },
      { id: 'extras', visible: () => extrasAvailable() },
      { id: 'personalization', visible: () => Boolean(availableEngraving()) }
    ];

    const updateVisibleSteps = () => {
      visibleStepConfig.forEach((step) => {
        const node = elements.stepNodes.find((entry) => entry.dataset.meerbegleiterStep === step.id);
        if (!node) return;
        const visible = step.visible();
        node.hidden = !visible;
      });
    };

    const createExtraToken = (item) => ({
      id: `extra-${nextExtraTokenId++}`,
      itemId: item.id,
      type: item.type,
      value: item.token,
      name: item.label,
      finish: item.finish,
      assetUrl: item.imageUrl,
      shopifyVariantId: item.shopifyVariantId,
      sku: item.sku,
      unitPriceCents: item.unitPriceCents,
      inventoryStatus: item.inventoryStatus,
      catalogStatus: item.catalogStatus,
      physical: true
    });

    const createExtraSpaceToken = () => ({
      id: `extra-${nextExtraTokenId++}`,
      itemId: null,
      type: 'space',
      value: ' ',
      name: 'Leerzeichen',
      finish: null,
      assetUrl: null,
      physical: false
    });

    const cleanExtraTokenSpacing = () => {
      const cleaned = [];
      state.extras.forEach((token) => {
        if (token.physical === false && (cleaned.length === 0 || cleaned.at(-1)?.physical === false)) return;
        cleaned.push(token);
      });
      while (cleaned.at(-1)?.physical === false) cleaned.pop();
      state.extras = cleaned;
    };

    const setExtrasFinish = (finishId) => {
      if (!catalog.extrasConfig.finishes.some((finish) => finish.id === finishId)) return;
      const previousCount = extrasCount();
      state.extrasFinish = finishId;
      const requestedByVariant = new Map();
      state.extras = state.extras
        .map((token) => {
          if (token.physical === false) return token;
          const item = catalogExtraItemFor(token.type, token.value, finishId);
          const nextQuantity = (requestedByVariant.get(item?.shopifyVariantId) || 0) + 1;
          if (!item || !extraAvailabilityFor(item, nextQuantity).available) return null;
          requestedByVariant.set(item.shopifyVariantId, nextQuantity);
          return item;
        })
        .filter(Boolean)
        .map((item) => item.physical === false ? createExtraSpaceToken() : createExtraToken(item));
      cleanExtraTokenSpacing();
      if (previousCount > extrasCount() && elements.extrasError) {
        elements.extrasError.textContent = 'Nicht verfügbare Schmuckelemente oder nicht ausreichende Mengen wurden aus deiner Kombination entfernt.';
      }
      extrasFinishInputs.forEach((input) => {
        input.checked = input.value === finishId;
        input.closest('label')?.classList.toggle('is-active', input.checked);
      });
    };

    const renderExtras = () => {
      const active = extrasEnabled();
      if (!active && state.extras.length > 0) state.extras = [];
      if (active && extrasCount() === 0 && state.extras.length > 0) state.extras = [];
      const combination = active ? extrasCombination() : '';
      const label = combination ? `${combination} · ${extrasFinishName()}` : active ? 'Noch keine Extras' : 'Ohne Extras';
      const count = extrasCount();
      const price = combination ? `${count} ${count === 1 ? 'Teil' : 'Teile'} · +${formatPrice(extrasTotalCents())}` : '';
      if (elements.extrasControls) elements.extrasControls.hidden = !active;
      extrasChoiceInputs.forEach((input) => {
        input.disabled = !extrasAvailable();
        input.checked = input.value === (active ? 'add' : 'none');
      });
      if (elements.extrasDisplay) elements.extrasDisplay.textContent = label;
      if (elements.extrasPrice) elements.extrasPrice.textContent = price;
      if (elements.extrasModalDisplay) elements.extrasModalDisplay.textContent = label;
      if (elements.extrasModalPrice) elements.extrasModalPrice.textContent = price;
      if (elements.extrasOpen) elements.extrasOpen.textContent = combination ? 'Extras ändern' : 'Extras auswählen';
      if (elements.extrasReset) elements.extrasReset.hidden = state.extras.length === 0;
      elements.extrasSpecialButtons.forEach((button) => {
        const item = catalog.extrasConfig.items.find((entry) => entry.id === button.dataset.meerbegleiterAddExtraSpecial);
        const visible = Boolean(active && item && item.finish === state.extrasFinish && item.availableDiameters?.includes(Number(state.diameterMm)));
        const availability = visible ? extraAvailabilityFor(item) : { available: false };
        button.hidden = !visible;
        button.disabled = visible && !availability.available;
        button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
        const label = button.querySelector('span');
        if (label) {
          const baseLabel = button.dataset.meerbegleiterBaseLabel || label.textContent;
          button.dataset.meerbegleiterBaseLabel = baseLabel;
          label.textContent = button.disabled ? `${baseLabel} · Ausverkauft` : baseLabel;
        }
      });

      const tokenButtons = physicalExtraStateTokens().map((token, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'suedlis-meerbegleiter-personalizer__extras-token';
        button.setAttribute('aria-label', `${token.name} an Position ${index + 1} entfernen`);
        if (token.assetUrl) {
          const image = document.createElement('img');
          image.src = token.assetUrl;
          image.alt = '';
          image.width = 800;
          image.height = 800;
          image.loading = 'lazy';
          image.decoding = 'async';
          button.appendChild(image);
        }
        const value = document.createElement('span');
        value.textContent = token.value;
        const remove = document.createElement('b');
        remove.setAttribute('aria-hidden', 'true');
        remove.textContent = '×';
        button.append(value, remove);
        button.addEventListener('click', () => {
          state.extras = state.extras.filter((entry) => entry.id !== token.id);
          cleanExtraTokenSpacing();
          updateAll();
        });
        return button;
      });
      elements.extrasTokens?.replaceChildren(...tokenButtons);
      if (elements.extrasOverviewImages) {
        const images = physicalExtraStateTokens().map((token) => {
          const image = document.createElement('img');
          image.src = token.assetUrl;
          image.alt = '';
          image.width = 800;
          image.height = 800;
          image.loading = 'lazy';
          image.decoding = 'async';
          return image;
        });
        elements.extrasOverviewImages.replaceChildren(...images);
        elements.extrasOverviewImages.hidden = images.length === 0;
      }
      extrasFinishInputs.forEach((input) => {
        const finishAvailable = catalog.extrasConfig.items.some((item) => (
          item.finish === input.value
          && item.availableDiameters?.includes(Number(state.diameterMm))
          && extraAvailabilityFor(item).available
        ));
        input.disabled = !active || !finishAvailable;
        input.required = active;
        input.checked = active && input.value === state.extrasFinish;
        input.closest('label')?.classList.toggle('is-active', input.checked);
      });
      if (elements.extrasInput) elements.extrasInput.disabled = !active;
    };

    const addTextExtras = () => {
      if (!elements.extrasInput || !extrasEnabled()) return;
      if (!state.extrasFinish) {
        elements.extrasError.textContent = 'Bitte wähle zuerst Silber oder Gold.';
        return;
      }
      const normalizedInput = elements.extrasInput.value.toUpperCase().replace(/\s+/gu, ' ').trim();
      elements.extrasInput.value = normalizedInput;
      const allowed = new Set(Array.from(catalog.extrasConfig.textCharacters || ''));
      const invalid = Array.from(normalizedInput).filter((character) => character !== ' ' && !allowed.has(character));
      if (invalid.length > 0) {
        elements.extrasError.textContent = `Nicht unterstützt: ${[...new Set(invalid)].join(' ')}. Bitte verwende A–Z, # oder &.`;
        return;
      }
      if (!normalizedInput) {
        elements.extrasError.textContent = 'Bitte gib mindestens einen Buchstaben oder ein Zeichen ein.';
        return;
      }
      const items = Array.from(normalizedInput).map((value) => (
        value === ' ' ? null : catalogExtraItemFor(/[A-Z]/u.test(value) ? 'letter' : 'symbol', value)
      ));
      if (items.some((item, index) => normalizedInput[index] !== ' ' && !item)) {
        elements.extrasError.textContent = 'Für mindestens ein Element fehlt die gewählte Metallvariante.';
        return;
      }
      const shortfall = extraInventoryShortfallForItems([
        ...selectedExtraCatalogItems(),
        ...items.filter(Boolean)
      ]);
      if (shortfall) {
        elements.extrasError.textContent = extraInventoryMessage(shortfall);
        return;
      }
      state.extras.push(...items.map((item, index) => (
        normalizedInput[index] === ' ' ? createExtraSpaceToken() : createExtraToken(item)
      )));
      cleanExtraTokenSpacing();
      elements.extrasInput.value = '';
      elements.extrasError.textContent = '';
      updateAll();
    };

    const addSpecialExtra = (itemId) => {
      if (!extrasEnabled()) return;
      const item = catalog.extrasConfig.items.find((entry) => (
        entry.id === itemId
        && entry.finish === state.extrasFinish
        && entry.availableDiameters?.includes(Number(state.diameterMm))
      ));
      if (!item) return;
      const shortfall = extraInventoryShortfallForItems([...selectedExtraCatalogItems(), item]);
      if (shortfall) {
        elements.extrasError.textContent = extraInventoryMessage(shortfall);
        return;
      }
      state.extras.push(createExtraToken(item));
      elements.extrasError.textContent = '';
      updateAll();
    };

    const openExtrasModal = () => {
      if (!elements.extrasModal || !extrasEnabled()) return;
      extrasModalLastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      elements.extrasModal.hidden = false;
      elements.extrasModal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('suedlis-meerbegleiter-rope-modal-open');
      window.requestAnimationFrame(() => elements.extrasInput?.focus());
    };

    const closeExtrasModal = ({ restoreFocus = true } = {}) => {
      if (!elements.extrasModal) return;
      elements.extrasModal.hidden = true;
      elements.extrasModal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('suedlis-meerbegleiter-rope-modal-open');
      elements.extrasError.textContent = '';
      if (restoreFocus) extrasModalLastFocus?.focus();
      extrasModalLastFocus = null;
    };

    const trapExtrasModalFocus = (event) => {
      if (event.key !== 'Tab' || elements.extrasModal?.hidden) return;
      const focusable = Array.from(elements.extrasModalDialog.querySelectorAll('button:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex="-1"])'));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const extrasSelectionBlockers = ({ requireUnlistedVariant = true } = {}) => {
      if (!state.extrasEnabled) return [];
      const blockers = [];
      if (!extrasAvailable()) blockers.push('Extras-Freigabe für die Tau-Stärke');
      if (!state.extrasFinish) blockers.push('Extra-Metallfarbe');
      if (extrasCount() === 0) blockers.push('mindestens ein Extraelement');
      if (extrasCount() > 0) {
        const invalidExtra = physicalExtraStateTokens().some((token) => !catalog.extrasConfig.items.some((item) => Boolean(
          item.id === token.itemId
          && item.token === token.value
          && item.finish === state.extrasFinish
          && item.shopifyVariantId === token.shopifyVariantId
          && Number.isInteger(item.unitPriceCents)
          && item.catalogStatus === 'production-available'
          && (!requireUnlistedVariant || item.inventoryStatus === 'unlisted')
          && item.availableDiameters?.includes(Number(state.diameterMm))
        )));
        if (invalidExtra) blockers.push('reale Shopify-Extra-Variante');
        if (extrasInventoryShortfall()) blockers.push('verfügbare Extraelemente in ausreichender Stückzahl');
      }
      return blockers;
    };

    const summaryValue = (key, value, { alwaysVisible = false } = {}) => {
      const output = root.querySelector(`[data-meerbegleiter-summary="${key}"]`);
      if (output) output.textContent = value;
      const row = root.querySelector(`[data-meerbegleiter-summary-row="${key}"]`);
      if (row) row.hidden = !alwaysVisible && !value;
    };

    const updateSummary = () => {
      const model = selectedModel();
      const rope = selectedRope();
      const fitting = selectedFitting();
      const engraving = currentEngraving();
      const coordinates = coordinateEngraving();
      const engravingValue = engraving
        ? state.engravingMode === 'coordinates'
          ? [coordinates.latitude, coordinates.longitude, coordinates.place.trim() ? coordinates.place : ''].filter(Boolean).join('\n')
          : elements.text.value
        : '';
      const showEngravingSummary = Boolean(engraving && (fitting?.engravable || engravingValue));
      const extrasValue = extrasEnabled() && state.extras.length > 0
        ? `${extrasCombination()} · ${extrasFinishName()} · +${formatPrice(extrasTotalCents())}`
        : '';
      const totalCents = calculateTotalCents();

      summaryValue('model', model ? (model.displayName || model.name) : '');
      summaryValue('rope', rope?.name || '');
      summaryValue('diameter', state.diameterMm ? `${state.diameterMm} mm` : '');
      summaryValue('fitting', model?.requiredFittingTypes?.length === 0 ? 'Kein separates Endstück' : fittingDisplayName(fitting));
      summaryValue('extras', extrasValue);
      summaryValue('personalization', engraving ? `Gravur hinzugefügt · ${engravingModeLabel()}` : '');
      summaryValue('engraving', showEngravingSummary ? engravingValue : '');
      summaryValue('price', totalCents === null ? 'Wähle die Tau-Stärke.' : formatPrice(totalCents), { alwaysVisible: true });
      const hasSelection = Boolean(model || rope || state.diameterMm || fitting || extrasValue || engravingValue);
      if (elements.summaryEmpty) elements.summaryEmpty.hidden = hasSelection;
    };

    const configurationBlockers = () => {
      const blockers = [];
      const model = selectedModel();
      const rope = selectedRope();
      const fitting = selectedFitting();
      const engraving = currentEngraving();
      const configuration = resolveShopifyConfiguration();

      if (!model) blockers.push('Meerbegleiter-Modell');
      else if (model.rulesStatus !== 'confirmed') blockers.push(`bestätigte Kompatibilitätsdaten für „${model.name}“`);
      if (model && !state.diameterMm) blockers.push('bestätigte Tau-Stärke');
      else if (state.diameterMm && !isDiameterCompatible(state.diameterMm)) blockers.push('zulässige Tau-Stärke');
      if (state.diameterMm && (!rope || !isRopeCompatible(rope))) blockers.push('bestätigte Tau-Farbausführung');
      if (rope?.testOnly) blockers.push('produktionsfreigegebene Tau-Farbausführung');

      if (model?.requiredFittingTypes.length > 0) {
        if (!fitting) blockers.push(model.requiredFittingTypes.includes('centerpiece') ? 'bestätigtes Mittelstück' : 'kompatibles Endstück');
        else if (!isFittingCompatible(fitting)) blockers.push('kompatibles Endstück');
        else if (!model.requiredFittingTypes.includes(fitting.type)) blockers.push('bauformspezifisches Endstück');
        else if (fitting.testOnly) blockers.push(model.requiredFittingTypes.includes('centerpiece') ? 'produktionsfreigegebenes Mittelstück' : 'produktionsfreigegebenes Endstück');
      }

      if (state.engravingEnabled) {
        if (!engraving) blockers.push('bestätigte Gravurfläche');
        else if (engraving.testOnly) blockers.push('produktionsfreigegebenes Gravurprofil');
        else if (state.engravingMode === 'text' && !elements.text.value.trim()) blockers.push('Gravurtext');
        if (engraving && state.engravingMode === 'coordinates') {
          if (!coordinateValue('latitude')) blockers.push('gültiger Breitengrad');
          if (!coordinateValue('longitude')) blockers.push('gültiger Längengrad');
          if (!elements.confirmation.checked) blockers.push('Koordinaten-Bestätigung');
        }
      }
      blockers.push(...extrasSelectionBlockers());

      const fittingComplete = model?.requiredFittingTypes.length === 0 || Boolean(fitting);
      if (model && state.diameterMm && rope && fittingComplete && !configuration) blockers.push('exakte Shopify-Variante mit Warenkorbpreis');
      if (configuration && !configuration.shopifyVariantId) blockers.push('Shopify-Varianten-ID');
      if (configuration && !Number.isFinite(configuration.priceCents)) blockers.push('bestätigter Warenkorbpreis');
      if (configuration && configuration.inventoryStatus !== 'unlisted') blockers.push('UNLISTED-Produktfreigabe');
      return { blockers, configuration };
    };

    const updateProperties = () => {
      const model = selectedModel();
      const rope = selectedRope();
      const fitting = selectedFitting();
      const engraving = currentEngraving();
      const { configuration } = configurationBlockers();
      const coordinatesActive = Boolean(engraving) && state.engravingMode === 'coordinates';
      const coordinates = coordinateEngraving();
      const extrasActive = extrasEnabled() && state.extras.length > 0;

      setProperty(properties.model, model?.name || '');
      setProperty(properties.modelId, model?.id || '');
      setProperty(properties.rope, rope?.name || '');
      setProperty(properties.ropeId, rope?.id || '');
      setProperty(properties.diameter, state.diameterMm ? `${state.diameterMm} mm` : '');
      setProperty(properties.fitting, fitting?.name || '');
      setProperty(properties.fittingId, fitting?.id || '');
      setProperty(properties.fittingSize, fitting ? `${state.diameterMm} mm` : '');
      setProperty(properties.fittingFinish, fitting ? [fitting.material, fitting.finish].filter(Boolean).join(' · ') : '');
      setProperty(properties.extras, extrasActive ? extrasCombination() : '');
      setProperty(properties.extrasFinish, extrasActive ? extrasFinishName() : '');
      setProperty(properties.extrasCount, extrasActive ? extrasCount() : '');
      setProperty(properties.engravingArea, engraving ? `${engraving.widthMm} × ${engraving.heightMm} mm · ${engraving.position}` : '');
      setProperty(properties.latitude, coordinatesActive ? coordinates.latitude : '');
      setProperty(properties.longitude, coordinatesActive ? coordinates.longitude : '');

      if (configuration?.shopifyVariantId) {
        elements.variantId.value = shopifyDraft?.ajaxVariantId
          ? shopifyDraft.ajaxVariantId(configuration.shopifyVariantId)
          : String(configuration.shopifyVariantId);
        elements.variantId.disabled = true;
      } else {
        elements.variantId.value = '';
        elements.variantId.disabled = true;
      }
    };

    const updateReadiness = () => {
      const { blockers } = configurationBlockers();
      const environmentAllowed = isCurrentCartEnvironmentAllowed();
      const ready = environmentAllowed && blockers.length === 0 && !cartRequestInFlight;
      elements.submit.disabled = !ready;
      elements.submit.textContent = cartRequestInFlight
        ? 'Warenkorb wird vorbereitet …'
        : environmentAllowed ? 'In den Warenkorb' : 'Warenkorb gesperrt';
      if (elements.availability) {
        elements.availability.textContent = environmentAllowed
          ? 'Auf Bestellung gefertigt · Deine Konfiguration wird im Warenkorb gespeichert.'
          : 'Sicherheitsstopp: Theme-ID und Theme-Rolle sind nicht für den Meerbegleiter-Warenkorb freigegeben.';
      }
    };

    const updateCounts = () => {
      elements.textCount.textContent = String(elements.text.value.length);
      elements.placeCount.textContent = String(elements.place.value.length);
    };

    const updateAll = () => {
      updateEngravingAvailability();
      updateVisibleSteps();
      renderExtras();
      updateCounts();
      updateProperties();
      updateSummary();
      updateReadiness();
      refreshChoiceStates();
    };

    const createConfigurationId = () => {
      if (window.crypto?.randomUUID) return `mb-${window.crypto.randomUUID()}`;
      return `mb-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    };

    const manufacturingProperties = () => {
      const rope = selectedRope();
      const fitting = selectedFitting();
      const engraving = currentEngraving();
      const coordinates = coordinateEngraving();
      const fittingKey = fitting?.type === 'centerpiece' ? 'Mittelstück' : 'Endstück';
      const result = {
        '_Modell-ID': selectedModel()?.id || '',
        'Modell / Bauform': selectedModel()?.name || '',
        '_Tau-ID': rope?.id || '',
        Tau: rope?.name || '',
        'Tau-Farbe': rope?.name || '',
        'Tau-Stärke': state.diameterMm ? `${state.diameterMm} mm` : '',
        ...(fitting ? {
          '_Beschlag-ID': fitting.id,
          Beschlag: fitting.name,
          Beschlaggröße: `${state.diameterMm} mm`,
          'Beschlagmaterial/-farbe': [fitting.material, fitting.finish].filter(Boolean).join(' · '),
          [fittingKey]: fitting.name
        } : {})
      };

      if (engraving) {
        result['_Gravurprofil-ID'] = fitting?.engravingProfileId || '';
        result.Gravurart = engravingModeLabel();
        result.Gravurfläche = `${engraving.widthMm} × ${engraving.heightMm} mm · ${engraving.position}`;
        if (state.engravingMode === 'text') result.Gravurtext = elements.text.value.trim();
        if (state.engravingMode === 'coordinates') {
          result.Breitengrad = coordinates.latitude;
          result.Längengrad = coordinates.longitude;
          result['Ort der Bedeutung'] = coordinates.place.trim();
          result['Koordinaten geprüft'] = elements.confirmation.checked ? 'Ja' : '';
        }
      }
      return result;
    };

    const cartPayload = () => {
      const baseVariant = resolveShopifyConfiguration();
      const extrasString = extrasEnabled() ? extrasCombination() : '';
      const rope = selectedRope();
      const fitting = selectedFitting();
      const engraving = currentEngraving();
      return shopifyDraft.buildCartPayload({
        baseVariant,
        extrasString,
        extrasFinishId: extrasString ? state.extrasFinish : '',
        extraCatalogItems: catalog.extrasConfig.items,
        fittingCatalogItems: catalog.fittings,
        properties: manufacturingProperties(),
        configurationId: createConfigurationId(),
        productionReady: Boolean(
          rope
          && !rope.testOnly
          && (!fitting || !fitting.testOnly)
          && (!engraving || !engraving.testOnly)
          && (!engraving || Boolean(approvedEngravingProfile(fitting)))
        )
      });
    };

    const cartRouteUrl = (path) => {
      const rootUrl = String(window.Shopify?.routes?.root || '/');
      return `${rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`}${String(path || '').replace(/^\//u, '')}`;
    };
    const cartAddUrl = () => cartRouteUrl('cart/add.js');

    const rollbackCartConfiguration = async (configurationId) => {
      if (!configurationId || !isCurrentCartEnvironmentAllowed()) {
        throw new Error('Sicherheitsstopp: Die fehlgeschlagene Konfiguration konnte nicht eindeutig zurückgesetzt werden.');
      }
      const readCart = async () => {
        const response = await fetch(cartRouteUrl('cart.js'), { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Warenkorb-Readback fehlgeschlagen (${response.status}).`);
        return response.json();
      };
      const cart = await readCart();
      const matchingLineKeys = shopifyDraft?.cartLineKeysForConfiguration?.(cart, configurationId) || [];
      if (matchingLineKeys.length === 0) return;
      const updates = Object.fromEntries(matchingLineKeys.map((key) => [key, 0]));
      const updateResponse = await fetch(cartRouteUrl('cart/update.js'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });
      if (!updateResponse.ok) throw new Error(`Warenkorb-Rollback fehlgeschlagen (${updateResponse.status}).`);
      const verifiedCart = await readCart();
      if (shopifyDraft?.cartContainsConfiguration?.(verifiedCart, configurationId) !== false) {
        throw new Error('Warenkorb-Rollback konnte nicht vollständig bestätigt werden.');
      }
    };

    const submitCart = async () => {
      if (cartRequestInFlight) return;
      if (!isCurrentCartEnvironmentAllowed()) {
        elements.formError.textContent = 'Sicherheitsstopp: Theme-ID und Theme-Rolle sind für diesen Warenkorb-Request nicht freigegeben.';
        elements.formError.hidden = false;
        updateReadiness();
        return;
      }

      let payload;
      try {
        payload = cartPayload();
      } catch (error) {
        elements.formError.textContent = error instanceof Error ? error.message : 'Der Warenkorb-Payload konnte nicht sicher erzeugt werden.';
        elements.formError.hidden = false;
        return;
      }

      cartRequestInFlight = true;
      elements.formError.hidden = true;
      updateReadiness();
      const configurationId = payload?.items?.[0]?.properties?.['_Meerbegleiter Konfig-ID'] || '';
      try {
        const response = await fetch(cartAddUrl(), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        const responseBody = await response.json().catch(() => null);
        if (!response.ok) {
          const message = responseBody?.description || responseBody?.message || `Shopify-Fehler ${response.status}`;
          const error = new Error(response.status === 422
            ? `Mindestens eine Variante ist nicht mehr in der benötigten Menge verfügbar. Bitte korrigiere deine Extras-Auswahl. (${message})`
            : message);
          error.cartInventoryRejected = response.status === 422;
          throw error;
        }
        const cartUpdated = new CustomEvent('suedlis:cart:updated', {
          bubbles: true,
          cancelable: true,
          detail: { trigger: elements.submit, message: 'Dein Meerbegleiter wurde hinzugefügt.' }
        });
        if (document.dispatchEvent(cartUpdated)) {
          window.location.assign(catalog.cartUrl || '/cart');
        }
      } catch (error) {
        let rollbackError = null;
        try {
          await rollbackCartConfiguration(configurationId);
        } catch (cleanupError) {
          rollbackError = cleanupError;
        }
        elements.formError.textContent = rollbackError
          ? `Sicherheitsstopp: Eine fehlgeschlagene Konfiguration konnte nicht vollständig aus dem Warenkorb entfernt werden. Bitte öffne den Warenkorb und entferne alle Positionen dieser Konfiguration. (${rollbackError.message})`
          : `${error instanceof Error ? error.message : 'Der Warenkorb konnte nicht aktualisiert werden.'} Alle Positionen dieser Konfiguration wurden aus dem Warenkorb zurückgesetzt.`;
        elements.formError.hidden = false;
        if (!rollbackError && error?.cartInventoryRejected && extrasEnabled()) {
          elements.extrasError.textContent = 'Bitte entferne das nicht mehr verfügbare Element oder reduziere die benötigte Menge.';
          openExtrasModal();
        }
      } finally {
        cartRequestInFlight = false;
        updateReadiness();
      }
    };

    const validate = () => {
      clearErrors();
      const model = selectedModel();
      const rope = selectedRope();
      const fitting = selectedFitting();
      const engraving = currentEngraving();
      const { blockers } = configurationBlockers();
      let focusTarget = null;

      if (model && !state.diameterMm) setFieldError('diameter', 'Bitte wähle eine Tau-Stärke.');
      if (state.diameterMm && !rope) {
        setFieldError('rope', 'Diese Auswahl ist aktuell noch nicht verfügbar.');
        focusTarget = elements.rope.disabled ? null : elements.rope;
      }
      if (model?.requiredFittingTypes.length > 0 && !fitting) setFieldError('fitting', 'Bitte wähle ein kompatibles Endstück.');
      if (engraving && state.engravingMode === 'text' && !elements.text.value.trim()) {
        setFieldError('text', 'Bitte gib deinen Gravurtext ein.');
        focusTarget = focusTarget || elements.text;
      }
      if (engraving && state.engravingMode === 'coordinates') {
        if (!validateCoordinateAxis('latitude', true)) focusTarget = focusTarget || elements.latitudeDegrees;
        if (!validateCoordinateAxis('longitude', true)) focusTarget = focusTarget || elements.longitudeDegrees;
        if (!elements.confirmation.checked) {
          setFieldError('confirmation', 'Bitte bestätige, dass du deine Koordinaten geprüft hast.');
          focusTarget = focusTarget || elements.confirmation;
        }
      }
      if (blockers.length > 0) {
        elements.formError.textContent = 'Bitte prüfe deine Auswahl und ergänze die noch fehlenden Angaben.';
        elements.formError.hidden = false;
        focusTarget?.focus();
      }
      return blockers.length === 0;
    };

    elements.rope.addEventListener('change', () => {
      state.ropeId = elements.rope.value;
      clearErrors();
      updateAll();
    });
    elements.ropeModalTrigger?.addEventListener('click', () => {
      clearErrors();
      openRopeModal(state.diameterMm);
    });
    root.querySelectorAll('[data-meerbegleiter-rope-modal-close]').forEach((button) => {
      button.addEventListener('click', () => closeRopeModal());
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && elements.ropeModal && !elements.ropeModal.hidden) {
        closeRopeModal();
      }
      if (event.key === 'Escape' && elements.extrasModal && !elements.extrasModal.hidden) {
        closeExtrasModal();
      } else {
        trapExtrasModalFocus(event);
      }
    });
    engravingChoiceInputs.forEach((input) => input.addEventListener('change', () => {
      if (!input.checked) return;
      if (input.value === 'add' && availableEngraving()) state.engravingEnabled = true;
      else resetEngravingState();
      clearErrors();
      updateAll();
    }));
    modeInputs.forEach((input) => input.addEventListener('change', () => {
      if (!input.checked) return;
      state.engravingMode = input.value;
      clearErrors();
      updateAll();
    }));
    extrasChoiceInputs.forEach((input) => input.addEventListener('change', () => {
      if (!input.checked) return;
      if (input.value === 'add' && extrasAvailable()) state.extrasEnabled = true;
      else resetExtrasState();
      clearErrors();
      updateAll();
    }));
    elements.extrasOpen?.addEventListener('click', openExtrasModal);
    elements.extrasDone?.addEventListener('click', () => closeExtrasModal());
    root.querySelectorAll('[data-meerbegleiter-extras-modal-close]').forEach((button) => {
      button.addEventListener('click', () => closeExtrasModal());
    });
    root.querySelector('[data-meerbegleiter-add-extras]')?.addEventListener('click', addTextExtras);
    elements.extrasInput?.addEventListener('input', () => {
      elements.extrasInput.value = elements.extrasInput.value.toUpperCase();
      elements.extrasError.textContent = '';
    });
    elements.extrasInput?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      addTextExtras();
    });
    root.querySelectorAll('[data-meerbegleiter-add-extra-special]').forEach((button) => {
      button.addEventListener('click', () => addSpecialExtra(button.dataset.meerbegleiterAddExtraSpecial));
    });
    extrasFinishInputs.forEach((input) => input.addEventListener('change', () => {
      if (!input.checked || !extrasEnabled()) return;
      setExtrasFinish(input.value);
      updateAll();
    }));
    elements.extrasReset?.addEventListener('click', () => {
      state.extras = [];
      updateAll();
    });
    elements.text.addEventListener('input', () => {
      const limit = Number(currentEngraving()?.maxCharacters);
      enforceMaxLength(elements.text, limit);
      setFieldError('text');
      updateAll();
    });
    Object.entries(coordinateAxes).forEach(([axisName, axis]) => {
      [axis.degrees, axis.minutes].forEach((input) => input.addEventListener('input', () => {
        validateCoordinateAxis(axisName, true);
        updateAll();
      }));
      axis.direction.addEventListener('change', () => {
        validateCoordinateAxis(axisName, true);
        updateAll();
      });
    });
    elements.place.addEventListener('input', () => {
      const limit = Number(currentEngraving()?.placeMaxCharacters);
      enforceMaxLength(elements.place, limit);
      setFieldError('place');
      updateAll();
    });
    elements.confirmation.addEventListener('change', () => {
      setFieldError('confirmation');
      updateAll();
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      clearErrors();
      if (!isCurrentCartEnvironmentAllowed()) {
        elements.formError.textContent = 'Sicherheitsstopp: Theme-ID und Theme-Rolle sind für diesen Warenkorb-Request nicht freigegeben.';
        elements.formError.hidden = false;
        return;
      }
      if (!validate()) return;
      void submitCart();
    });

    renderDiameters();
    renderRopes();
    renderFittings();
    setExtrasFinish(state.extrasFinish);
    updateAll();
  };

  const boot = (scope = document) => scope.querySelectorAll(rootSelector).forEach(initialize);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
